/**
 * Wiederkehrende Bausteine der Portalansichten.
 *
 * **Warum hier und nicht in `packages/ui`.** Das Designsystem dort liefert,
 * was auch ein Fremddienst braucht — Knopf, Hinweis, Zustand, Ladeanzeige,
 * Fehlermeldung. Was hier steht, ist Anordnung: eine Kachel, eine Arbeitsliste,
 * ein Statusabzeichen. Das taugt nur für dieses Portal, und ein Designsystem,
 * das seine eigene Anwendung mitliefert, ist keins mehr.
 *
 * Keine Farbwerte, nur Tokenvariablen — dieselbe Auflage wie in `stil.css`
 * (ADR-040, Auflage 4).
 */

import type { ReactNode } from "react";
import { Link } from "react-router";

import type { EinsatzZustand } from "@hwc/sync";
import type { Eingangsstatus, Prioritaet } from "@hwc/demo-data";

/**
 * Eine Dashboardkachel.
 *
 * `07` §7.4: „Keine dekorativen Kennzahlen ohne Handlungswert. Jede Karte
 * führt zu einer gefilterten Arbeitsliste." Deshalb ist `zu` keine Option,
 * sondern Pflicht — eine Kachel ohne Ziel wäre genau die Dekoration, die der
 * Satz ausschließt.
 */
export function Kachel({
  titel,
  zahl,
  zu,
  ton = "neutral",
  children,
}: {
  titel: string;
  zahl: number;
  zu: string;
  ton?: "neutral" | "warnung" | "fehler" | "erfolg";
  children?: ReactNode;
}) {
  return (
    <Link className={`kachel kachel--${ton}`} to={zu}>
      <span className="kachel__zahl">{zahl}</span>
      <span className="kachel__titel">{titel}</span>
      {children && <span className="kachel__zusatz">{children}</span>}
    </Link>
  );
}

export function Kachelreihe({ children }: { children: ReactNode }) {
  return <div className="kachelreihe">{children}</div>;
}

/**
 * Eine Zeile in einer Arbeitsliste.
 *
 * Die ganze Zeile ist der Verweis und nicht nur der Titel. Auf einem
 * Touchgerät ist ein 16 Pixel hoher Textlink kein Ziel — `07` §7.14 verlangt
 * große Trefferflächen, und das gilt nicht nur für die Monteur-App.
 */
export function Zeile({
  zu,
  titel,
  unterzeile,
  rechts,
  abzeichen,
}: {
  // `undefined` steht ausdruecklich dabei, obwohl `?` es sonst mitbringt: Mit
  // `exactOptionalPropertyTypes` sind „nicht gesetzt" und „auf undefined
  // gesetzt" zwei verschiedene Dinge. Jeder Aufrufer hier rechnet einen Wert
  // aus, der fehlen kann — ein Auftrag ohne Kunde, ein Einsatz ohne Monteur.
  // Ihn stattdessen am Aufrufort wegzulassen hiesse, jedes Feld einzeln in
  // eine Spreizung zu verpacken, und das an einem Dutzend Stellen.
  zu?: string | undefined;
  titel: ReactNode;
  unterzeile?: ReactNode | undefined;
  rechts?: ReactNode | undefined;
  abzeichen?: ReactNode | undefined;
}) {
  const inhalt = (
    <>
      <span className="zeile__haupt">
        <span className="zeile__titel">{titel}</span>
        {unterzeile && <span className="zeile__unterzeile">{unterzeile}</span>}
      </span>
      {abzeichen && <span className="zeile__abzeichen">{abzeichen}</span>}
      {rechts && <span className="zeile__rechts">{rechts}</span>}
    </>
  );

  return zu ? (
    <Link className="zeile zeile--verweis" to={zu}>
      {inhalt}
    </Link>
  ) : (
    <div className="zeile">{inhalt}</div>
  );
}

export function Liste({ children }: { children: ReactNode }) {
  return <div className="liste">{children}</div>;
}

/**
 * Was angezeigt wird, wenn nichts da ist.
 *
 * Ein leerer Bereich ohne Text ist nicht zu unterscheiden von einem, der nicht
 * geladen hat. Der Satz sagt deshalb, **warum** nichts da ist, nicht nur dass.
 */
export function Leer({ text }: { text: string }) {
  return <p className="leer leise">{text}</p>;
}

/**
 * Das Statusabzeichen eines Einsatzes.
 *
 * `07` §7.14 verbietet farbabhängige Statusinformation. Deshalb trägt jedes
 * Abzeichen ein Wort — die Farbe ist die Zugabe, nicht die Aussage. Wer das
 * umdreht, baut eine Oberfläche, die bei Rot-Grün-Schwäche und im Sonnenlicht
 * dasselbe Problem hat.
 */
const ZUSTANDSTEXT: Record<EinsatzZustand, string> = {
  geplant: "Geplant",
  anfahrt: "Anfahrt",
  "vor-ort": "Vor Ort",
  wartet: "Wartet",
  abschluss: "Abschluss",
  uebermittelt: "Übermittelt",
  geprueft: "Geprüft",
  storniert: "Storniert",
};

const ZUSTANDSTON: Record<EinsatzZustand, string> = {
  geplant: "neutral",
  anfahrt: "laeuft",
  "vor-ort": "laeuft",
  wartet: "warnung",
  abschluss: "warnung",
  uebermittelt: "warnung",
  geprueft: "erfolg",
  storniert: "aus",
};

export function Einsatzabzeichen({ zustand }: { zustand: EinsatzZustand }) {
  return (
    <span className={`abzeichen abzeichen--${ZUSTANDSTON[zustand]}`}>
      {ZUSTANDSTEXT[zustand]}
    </span>
  );
}

export function einsatzzustandText(zustand: EinsatzZustand): string {
  return ZUSTANDSTEXT[zustand];
}

const EINGANGSTEXT: Record<Eingangsstatus, string> = {
  neu: "Neu",
  in_pruefung: "In Prüfung",
  zugeordnet: "Zugeordnet",
  zurueckgestellt: "Zurückgestellt",
  abgewiesen: "Abgewiesen",
  quarantaene: "Quarantäne",
};

const EINGANGSTON: Record<Eingangsstatus, string> = {
  neu: "warnung",
  in_pruefung: "laeuft",
  zugeordnet: "erfolg",
  zurueckgestellt: "neutral",
  abgewiesen: "aus",
  quarantaene: "fehler",
};

export function Eingangsabzeichen({ status }: { status: Eingangsstatus }) {
  return (
    <span className={`abzeichen abzeichen--${EINGANGSTON[status]}`}>
      {EINGANGSTEXT[status]}
    </span>
  );
}

export function eingangsstatusText(status: Eingangsstatus): string {
  return EINGANGSTEXT[status];
}

/**
 * Die Priorität.
 *
 * `notfall` bekommt als einzige eine eigene Auszeichnung. Vier gleichwertig
 * gestaltete Stufen wären vier Stufen, die niemand unterscheidet — und die
 * eine, auf die es ankommt, ginge darin unter.
 */
export function Prioritaetsabzeichen({ wert }: { wert: Prioritaet }) {
  if (wert === "normal" || wert === "niedrig") return null;
  return (
    <span className={`abzeichen abzeichen--${wert === "notfall" ? "fehler" : "warnung"}`}>
      {wert === "notfall" ? "Notfall" : "Hohe Priorität"}
    </span>
  );
}

/** Ein Reitersatz — die Bereiche der Auftragsakte aus `07` §7.5. */
export function Reiter({
  punkte,
  aktiv,
  beiWahl,
}: {
  punkte: readonly { readonly schluessel: string; readonly text: string }[];
  aktiv: string;
  beiWahl: (schluessel: string) => void;
}) {
  return (
    <div className="reiter" role="tablist">
      {punkte.map((p) => (
        <button
          key={p.schluessel}
          type="button"
          role="tab"
          aria-selected={p.schluessel === aktiv}
          className={`reiter__punkt${p.schluessel === aktiv ? " reiter__punkt--aktiv" : ""}`}
          onClick={() => beiWahl(p.schluessel)}
        >
          {p.text}
        </button>
      ))}
    </div>
  );
}

/** Eine Beschriftung mit Wert — für Stammdatenblöcke. */
export function Feld({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="feld">
      <dt className="feld__name">{name}</dt>
      <dd className="feld__wert">{children}</dd>
    </div>
  );
}

export function Felder({ children }: { children: ReactNode }) {
  return <dl className="felder">{children}</dl>;
}

// ───────────────────────── Formatierung ─────────────────────────

const UHRZEIT = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
});

const DATUM = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Europe/Berlin",
});

/**
 * Nur der Wochentag, ohne Datum.
 *
 * Er steht ueberall neben `datum()` — zusammen ergaeben `weekday: "short"` mit
 * Tag und Monat und das vollstaendige Datum daneben „Sa., 05.09. 05.09.2026".
 * Das ist keine Doppelung, die stoert, sondern eine, die beim Ueberfliegen
 * kostet: Das Auge sucht den Unterschied zwischen zwei fast gleichen Zahlen
 * und findet keinen.
 */
const WOCHENTAG = new Intl.DateTimeFormat("de-DE", {
  weekday: "long",
  timeZone: "Europe/Berlin",
});

/*
 * `mehrzahl` liegt seit dem 05.09.2026 in `@hwc/ui` und wird von hier nur
 * weitergereicht. Der Grund steht dort: Beim Nachsehen fand sich derselbe
 * Fehler auch in der Betreiber-Konsole, und drei Anwendungen brauchen
 * dieselbe Regel.
 */
export { mehrzahl } from "@hwc/ui";

export function uhrzeit(iso: string): string {
  return UHRZEIT.format(new Date(iso));
}

export function datum(iso: string): string {
  // Tagesangaben ohne Uhrzeit kommen als `YYYY-MM-DD`. `new Date()` liest die
  // als UTC-Mitternacht, und in Berlin ist das derselbe Tag — aber westlich
  // von Greenwich wäre es der Vortag. Die Mittagszeit macht die Angabe
  // gegenüber jeder Zone eindeutig.
  return DATUM.format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso));
}

export function wochentag(iso: string): string {
  return WOCHENTAG.format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso));
}

export function zeitfenster(von: string, bis: string): string {
  return `${uhrzeit(von)}–${uhrzeit(bis)}`;
}

/**
 * Minuten als Stunden und Minuten.
 *
 * `2:45 h` und nicht `2,75 h`: Der Monteur hat zwei Stunden und
 * fünfundvierzig Minuten gearbeitet, und niemand rechnet Dezimalstunden im
 * Kopf zurück. Für die Abrechnung wandelt die Branchensoftware um, was sie
 * braucht — hier wird gelesen, nicht gerechnet.
 */
export function dauer(minuten: number): string {
  const h = Math.floor(minuten / 60);
  const m = minuten % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")} h` : `${m} min`;
}

/** Cent als Euro. */
export function euro(cent: number): string {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
    .format(cent / 100);
}

/** Bytes in einer Größe, die man vorlesen kann. */
export function groesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

/**
 * Eine Sicherheitsangabe als Wort.
 *
 * `07` §7.4 verlangt, dass Vorschläge als Vorschlag erkennbar bleiben. „86 %"
 * liest sich wie eine Messung und lädt zum Durchwinken ein; „recht sicher"
 * sagt dasselbe und behauptet keine Genauigkeit, die eine Heuristik nicht hat.
 */
export function sicherheitWort(wert: number): string {
  if (wert >= 0.9) return "sehr wahrscheinlich";
  if (wert >= 0.75) return "wahrscheinlich";
  if (wert >= 0.5) return "möglich";
  return "unsicher";
}

/** Der Weg zur Auftragsakte. An einer Stelle, damit er nicht driftet. */
export function zumAuftrag(nummer: string): string {
  return `/auftraege/${nummer}`;
}

export function zumKunden(nummer: string): string {
  return `/kunden/${nummer}`;
}
